import { useCallback } from 'react';

import { getSessionState, setSessionState } from 'components/Analytics/google-analytics-store';
import orderStatus from 'constants/orderStatus';
import { useWtrSelector } from 'redux/hooks';
import { getPageTitle } from 'redux/middleware/analytics/actions-to-track/page/get-page-title';
import { getCookies } from 'redux/modules/cookies/selectors';
import { getCustomerSlotBranchId, getSlotType } from 'redux/modules/customer-slot/selectors';
import { isJointOrMyWaitroseMember } from 'redux/modules/marketing-preferences/selectors/get-membership-status';
import { hasPartnerDiscount } from 'redux/modules/order/selectors/partner-discount';
import { getRoutingPageType } from 'redux/modules/routing/selectors';
import {
  getCustomerId,
  getCustomerOrderId,
  getCustomerOrderState,
} from 'redux/modules/sessions/selectors';
import {
  canInstantCheckout,
  getInstantCheckoutReason,
} from 'redux/modules/trolley/selectors/can-instant-checkout';
import locator from 'utils/locator';

export default function useCheckoutAnalyticsStore() {
  const { analytics: hasAnalyticsCookie } = useWtrSelector(getCookies);
  const pageType = useWtrSelector(getRoutingPageType);
  const pageTitle = useWtrSelector(getPageTitle);
  const customerId = useWtrSelector(getCustomerId);
  const orderId = useWtrSelector(getCustomerOrderId);
  const branchId = useWtrSelector(getCustomerSlotBranchId);
  const isMyWaitroseMember = useWtrSelector(isJointOrMyWaitroseMember);
  const isInstantCheckoutAvailable = useWtrSelector(canInstantCheckout);
  const instantCheckoutReason = useWtrSelector(getInstantCheckoutReason);
  const slotType = useWtrSelector(getSlotType);
  const partnershipDiscount = useWtrSelector(hasPartnerDiscount);

  const orderState = useWtrSelector(getCustomerOrderState);
  let purchaseOrAmend = '';
  if (orderState === orderStatus.PENDING) {
    purchaseOrAmend = 'purchase';
  } else if (orderState === orderStatus.AMENDING) {
    purchaseOrAmend = 'amend';
  }

  const setCheckoutAnalyticsProps = useCallback(() => {
    // store session properties for GA4 so they are available for all events on the checkout page
    if (hasAnalyticsCookie) {
      const props = getSessionState()?.props || {};

      // common parameters for all events, as specified in GA4 implementation docs:
      //   https://johnlewispartnership.atlassian.net/wiki/spaces/WD/pages/621544687/Convert+existing+Checkout+events+to+Measurement+Protocol#The-Solution---MVP
      props.branch_id = branchId || ''; // from customer slot state
      // channel: calculated from URL param isMob=Y in beacon event params
      props.content_group = pageType; // from location, initial page_view event will overwrite this
      props.customer_id = customerId || ''; // from session state
      props.instant_checkout_availability_reason = instantCheckoutReason || ''; // from trolley state
      props.instant_checkout_available = isInstantCheckoutAvailable; // from trolley state
      props.instant_checkout_eligibility = isInstantCheckoutAvailable; // from trolley state
      props.mywaitrose_member = !!isMyWaitroseMember; // from membership state
      props.order_id = orderId || ''; // from shopping context state
      props.page_location = locator.href; // from location, initial page_view event will overwrite this
      props.page_path = locator.pathname; // from location, initial page_view event will overwrite this
      props.page_title = pageTitle; // from location, initial page_view event will overwrite this
      props.page_type = pageType; // from location, initial page_view event will overwrite this
      props.partner_discount_status = partnershipDiscount ? 'active' : 'in-active'; // uses order state
      props.previous_path = locator.pathname; // from location, initial page_view event will overwrite this
      props.purchase_or_amend = purchaseOrAmend; // from shopping context state
      // user_id: intentionally set in the event from the customer cookie or customer_id session property

      // additional properties that specific events need later
      props.service_type = slotType || ''; // from customer slot state

      setSessionState({ props });
    }
  }, [
    hasAnalyticsCookie,
    branchId,
    pageType,
    customerId,
    instantCheckoutReason,
    isInstantCheckoutAvailable,
    isMyWaitroseMember,
    orderId,
    pageTitle,
    partnershipDiscount,
    purchaseOrAmend,
    slotType,
  ]);

  return { setCheckoutAnalyticsProps };
}
